/* global BigInt */
import { Avatar, Badge, Box, Divider, Typography } from '@mui/material'
import { useNavigate, useLocation } from 'react-router-dom'
import {
    AddBox, Android, Apple, CalendarMonth, Celebration, EmojiFoodBeverage,
    Logout, Notifications, Send, Settings,
    Timeline, VideoCall, WavingHand,
} from '@mui/icons-material'
import { getLogout } from '../../controllers/accounts/auth'
import { useContext } from 'react'
import { UserContext } from '../../context/UserContext'
import { useQuery } from '@tanstack/react-query'
import { getMyConversations, getMyEventConversations } from '../../controllers/conversation/conversation'
import { getAllNotifications, getNotificationMarker } from '../../controllers/notify/notifications'

const PRIDE = ['#E40303', '#FF8C00', '#FFED00', '#008026', '#24408E', '#732982']

const IOS_APP_URL = 'https://apps.apple.com/us/app/make-gay-friends/id6478235888'
const ANDROID_APP_URL = 'https://play.google.com/store/apps/details?id=com.wizcomputer.makegayfriends&hl=en_US'

const AppStoreLink = ({ href, icon, label }) => (
    <Box
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.25,
            py: 0.85,
            borderRadius: 2,
            textDecoration: 'none',
            color: '#3a3a4a',
            fontSize: 12.5,
            fontWeight: 600,
            border: '1px solid #ececf0',
            '&:hover': { background: 'rgba(115,41,130,0.06)', borderColor: 'rgba(115,41,130,0.3)' },
        }}
    >
        {icon}
        {label}
    </Box>
)

const NavItem = ({ label, icon, url, onClick, badge }) => {
    const navigate = useNavigate()
    const location = useLocation()
    const active = url && location.pathname === url

    return (
        <Box
            onClick={onClick ?? (() => navigate(url))}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.1,
                cursor: 'pointer',
                borderRadius: 2,
                mx: 1,
                mb: 0.25,
                background: active ? 'rgba(115,41,130,0.08)' : 'transparent',
                borderLeft: active ? '3px solid #732982' : '3px solid transparent',
                color: active ? '#732982' : '#3a3a4a',
                fontWeight: active ? 700 : 500,
                fontSize: 14,
                transition: 'background 0.15s',
                '&:hover': {
                    background: 'rgba(115,41,130,0.06)',
                    color: '#732982',
                },
                '& .MuiSvgIcon-root': {
                    fontSize: 20,
                    color: active ? '#732982' : '#888',
                    transition: 'color 0.15s',
                },
                '&:hover .MuiSvgIcon-root': { color: '#732982' },
            }}
        >
            <Badge badgeContent={badge || 0} color="error" invisible={!badge}>
                {icon}
            </Badge>
            <span style={{ fontSize: 14, fontWeight: active ? 700 : 500 }}>{label}</span>
        </Box>
    )
}

export const MainMenu = () => {
    const navigate = useNavigate()
    const { userDetails } = useContext(UserContext)
    const token = userDetails?.token ?? ''
    // Mastodon's own /conversations only tracks direct-visibility statuses,
    // but messages here can be public (see CheckinAlertListener/MessageCreate),
    // so unread state has to come from the sidecar's own conversation records,
    // not Mastodon's — otherwise it misses (or misattributes) most of them.
    const { data: myConversations } = useQuery({
        queryKey: ['getMyConversations', userDetails?.id, token],
        queryFn: () => getMyConversations(token, userDetails?.id),
        enabled: !!token && !!userDetails?.id,
    })
    const unreadCount = (myConversations?.conversations ?? []).filter(c => c.unread).length

    const { data: myEventConversations } = useQuery({
        queryKey: ['getMyEventConversations', userDetails?.id, token],
        queryFn: () => getMyEventConversations(token, userDetails?.id),
        enabled: !!token && !!userDetails?.id,
    })
    const unreadMingleCount = (myEventConversations?.conversations ?? []).filter(c => c.unread).length

    const { data: notifications } = useQuery({
        queryKey: ['getAllNotifications'],
        queryFn: () => getAllNotifications(token),
        enabled: !!token,
    })
    const { data: notificationMarker } = useQuery({
        queryKey: ['getNotificationMarker'],
        queryFn: () => getNotificationMarker(token),
        enabled: !!token,
    })
    const lastReadId = BigInt(notificationMarker?.notifications?.last_read_id || 0)
    const unreadNotificationCount = (Array.isArray(notifications) ? notifications : [])
        .filter(n => BigInt(n.id) > lastReadId).length

    if (!userDetails?.id) return null

    const mainNav = [
        { label: 'Meet', icon: <WavingHand />, url: '/meet' },
        { label: 'The Tea', icon: <EmojiFoodBeverage />, url: '/feed' },
        { label: 'Events', icon: <CalendarMonth />, url: '/event/list' },
        { label: 'Your Party', icon: <Celebration />, url: '/mingle', badge: unreadMingleCount },
        { label: 'Messages', icon: <Send />, url: '/messages', badge: unreadCount },
        { label: 'Quick Meets', icon: <VideoCall />, url: '/speed-meeting', hidden: true },
        { label: 'Upload', icon: <AddBox />, url: '/post/create', hidden: true },
    ].filter(item => !item.hidden)

    const bottomNav = [
        { label: 'Notifications', icon: <Notifications />, url: '/notifications', badge: unreadNotificationCount },
        { label: 'Settings', icon: <Settings />, url: '/settings' },
    ]

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, py: 1.5 }}>
            {/* Brand */}
            <Box sx={{ px: 2.5, mb: 2 }}>
                <Typography
                    sx={{
                        fontWeight: 900,
                        fontSize: 17,
                        background: 'linear-gradient(90deg,#E40303,#732982)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        letterSpacing: '-0.5px',
                        lineHeight: 1.2,
                    }}
                >
                    MakeGayFriends
                </Typography>
            </Box>

            {/* User card */}
            <Box
                onClick={() => navigate('/profile/' + userDetails.id)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1.25,
                    mx: 1,
                    mb: 1.5,
                    borderRadius: 2,
                    cursor: 'pointer',
                    background: 'linear-gradient(120deg,#f3f0ff,#fff0f6)',
                    border: '1px solid rgba(115,41,130,0.12)',
                    '&:hover': { background: 'linear-gradient(120deg,#ede8ff,#ffe4f0)' },
                }}
            >
                <Avatar
                    alt={userDetails?.username}
                    src={userDetails?.avatar}
                    sx={{ width: 36, height: 36, border: '2px solid #732982' }}
                />
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#23232b', lineHeight: 1.2 }} noWrap>
                        {userDetails?.display_name || userDetails?.username || 'Me'}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#888', lineHeight: 1.2 }} noWrap>
                        @{userDetails?.username}
                    </Typography>
                </Box>
            </Box>

            {/* Main nav */}
            <Box sx={{ flex: 1 }}>
                {mainNav.map((item) => (
                    <NavItem key={item.url} {...item} />
                ))}
            </Box>

            {/* Bottom nav */}
            <Box>
                <Divider sx={{ my: 1, mx: 2 }} />
                {bottomNav.map((item) => (
                    <NavItem key={item.url} {...item} />
                ))}
                <NavItem label="Logout" icon={<Logout />} onClick={getLogout} />
            </Box>

            {/* Get the app */}
            <Box sx={{ px: 2, mb: 1.5 }}>
                <Divider sx={{ mb: 1.5 }} />
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#8A95A3', mb: 1, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Get the app
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <AppStoreLink href={IOS_APP_URL} icon={<Apple sx={{ fontSize: 18 }} />} label="Download on iOS" />
                    <AppStoreLink href={ANDROID_APP_URL} icon={<Android sx={{ fontSize: 18, color: '#3DDC84' }} />} label="Get it on Android" />
                </Box>
            </Box>

            {/* Pride dots */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, py: 1.5 }}>
                {PRIDE.map((c) => (
                    <Box key={c} sx={{ width: 6, height: 6, borderRadius: '50%', background: c }} />
                ))}
            </Box>
        </Box>
    )
}
